import { Box, Menu, MenuItem } from '@mui/material';
import { rateCardAllowedFor } from '@timelog/pm-shared';
import type { ContractType } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ChangeBillingMethodDialog } from './ChangeBillingMethodDialog';
import { hasBudgets, seedRateCardFromDefaults, useContractEditing, useFinancials } from './editor';
import type { BillingMethodChange } from './editor';

import { useTimeLogRates } from '@/api/timelogHooks';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';

const TYPES: ContractType[] = ['fixed', 'tm', 'retainer'];

/**
 * Contract name (click-to-edit) + billing-method dropdown, autosaved with the
 * document. Changing the method on a contract that already carries budgets is
 * confirmed first, because the rate card does not survive it: a move to fixed
 * price deletes the card (BR-11), a move to T&M/retainer creates one from the
 * caller's default hourly rates (`seedRateCardFromDefaults`). A fresh contract
 * switches silently — and still gets its card on the way to T&M.
 *
 * The name shows for any number of contracts: hiding it on a sole contract
 * (2026-09-24) left no way to rename it, and was reverted on 2026-10-01 at the
 * pilot's request, together with the contracts rail.
 */
export const ContractHeader = () => {
  const { state, dispatch } = useFinancials();
  const { isEditing, startEditing, stopEditing } = useContractEditing();
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  const [pendingChange, setPendingChange] = useState<BillingMethodChange | null>(null);
  const defaultRates = useTimeLogRates(true);
  const { t } = useTranslation('pmCommon');

  const applyChange = (to: ContractType) => {
    dispatch({ type: 'SET_TYPE', contractType: to });
    if (rateCardAllowedFor(to) && !rateCardAllowedFor(state.type)) {
      const defaults = defaultRates.data?.items ?? [];
      dispatch({
        type: 'APPLY_OP',
        op: (document) => seedRateCardFromDefaults(document, defaults),
      });
    }
  };

  const requestChange = (to: ContractType) => {
    setTypeAnchor(null);
    if (to === state.type) return;
    if (!hasBudgets(state.document)) {
      applyChange(to);
      return;
    }
    setPendingChange({
      from: state.type,
      to,
      cardSize: state.document.rateCard.length,
      defaultRoleCount: defaultRates.data?.items.length ?? 0,
    });
  };

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
      }}
    >
      <InlineTextCell
        automationId="contract-name"
        value={state.name}
        editing={isEditing('name')}
        onStartEdit={() => startEditing('name')}
        onCancel={stopEditing}
        onCommit={(raw) => {
          dispatch({ type: 'SET_NAME', name: raw });
          stopEditing();
        }}
        inputWidth={220}
        inputSx={{ fontSize: 16, fontWeight: 500 }}
        display={
          <Box component="span" sx={{ fontSize: 16, fontWeight: 500, color: 'var(--fg-1)' }}>
            {state.name}
          </Box>
        }
      />
      <Box
        component="button"
        type="button"
        data-automation-id="contract-type"
        onClick={(event: MouseEvent<HTMLElement>) => setTypeAnchor(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          border: 'var(--border-width-thin) solid var(--border-divider)',
          borderRadius: 'var(--border-radius-pill)',
          bgcolor: 'transparent',
          fontSize: 11,
          fontFamily: 'var(--font-family)',
          color: 'var(--fg-2)',
          cursor: 'pointer',
          '&:hover': { borderColor: 'var(--border-subtle)', color: 'var(--fg-1)' },
        }}
      >
        {t(`billingMethod.${state.type}`)}
        <MaterialSymbol name="expand_more" size={14} />
      </Box>
      <Menu anchorEl={typeAnchor} open={typeAnchor !== null} onClose={() => setTypeAnchor(null)}>
        {TYPES.map((type) => (
          <MenuItem
            key={type}
            selected={type === state.type}
            data-automation-id="contract-type-option"
            onClick={() => requestChange(type)}
          >
            <MaterialSymbol
              name="check"
              size={14}
              color={type === state.type ? 'var(--fg-2)' : 'transparent'}
            />
            <Box component="span" sx={{ ml: 1 }}>
              {t(`billingMethod.${type}`)}
            </Box>
          </MenuItem>
        ))}
      </Menu>
      <ChangeBillingMethodDialog
        change={pendingChange}
        onCancel={() => setPendingChange(null)}
        onConfirm={() => {
          if (pendingChange) applyChange(pendingChange.to);
          setPendingChange(null);
        }}
      />
    </Box>
  );
};
